<template>

  <section>
    <div class="bet-list bet-list--cards">
      <div
          v-for="group in groupedOdds"
          :key="group.event_source"
          class="bet-card"
          :class="{ 'bet-card--multi': group.odds.length > 1, 'bet-card--bb': group.is_bet_builder }"
          @click="goToDetail(group.odds[0])"
      >
        <!-- Partita in alto, giocate sotto la linea -->
        <div class="sheet-match">
          <BetBuilderBadge v-if="group.is_bet_builder" default-text="BB" tag="div" class="bb-badge-cart" />
          <img
            v-else-if="group.odds[0].sport && group.odds[0].sport.id"
            :src="sportPath + group.odds[0].sport.id + '.svg'"
            class="sheet-match__icon"
            width="16" height="16"
          >
          <div class="sheet-match__body">
            <span class="sheet-match__teams" v-if="eventTeams(group.odds[0]).length === 2">
              {{ eventTeams(group.odds[0])[0] }}<span class="sheet-match__vs">vs</span>{{ eventTeams(group.odds[0])[1] }}
            </span>
            <span class="sheet-match__teams" v-else>{{ group.event_description }}</span>
            <span class="sheet-match__meta">
              <span v-if="group.odds[0].type === 'live'" class="sheet-match__live">
                <span class="sheet-match__live-dot"></span>{{ $t('live') || 'LIVE' }}
              </span>
              <span class="sheet-match__tournament" v-if="group.odds[0].tournament && group.odds[0].tournament.name">{{ group.odds[0].tournament.name }}</span>
              <template v-if="group.odds[0].type !== 'live' && group.odds[0].time">
                <span class="sheet-match__sep">·</span>
                <span>{{ formatEventTime(group.odds[0].time) }}</span>
              </template>
            </span>
          </div>
          <div class="bet-remove sheet-remove sheet-match__remove" :aria-label="$t('remove')" @click.stop="removeGroup(group)">
            <v-icon small class="btn-remove">mdi-close</v-icon>
          </div>
        </div>

        <!-- BET BUILDER: timeline verticale con dot e linea -->
        <div v-if="group.is_bet_builder" key="bb-container" class="bb-timeline-container">
          <div
              v-for="(odd, index) in group.odds"
              :key="odd.unique_id"
              class="bb-timeline-row"
              :class="{ 'bb-timeline-row-last': index === group.odds.length - 1, 'bb-timeline-row--redundant': odd.bb_redundant_leg }"
          >
            <div class="bb-timeline-dot"></div>
            <div class="bb-timeline-content">
              <span class="bb-outcome-name">{{ getOutcomeName(odd.competitors, odd, odd.unique_id) }}</span>
              <span class="bb-market-name">
                {{ odd.market_alias || odd.market_name }}
                <span v-if="getOddLine(odd)" class="bb-line-val">{{ getOddLine(odd) }}</span>
              </span>
              <!-- Redundant selection named by the provider: say what covers it, remove it in one click -->
              <span v-if="odd.bb_redundant_leg" class="bb-redundant-leg-hint">
                <v-icon x-small color="warning" class="mr-1">mdi-alert</v-icon>{{ redundantLegHint(group, odd) }}
              </span>
            </div>
            <div v-if="odd.bb_redundant_leg" class="bet-remove bb-redundant-leg-remove" :aria-label="$t('remove')" @click.stop="removeOdd(odd.unique_id)">
              <v-icon small class="btn-remove">mdi-close</v-icon>
            </div>
          </div>
          <!-- Quota combinata in fondo -->
          <div class="bb-combo-odd-row">
            <!-- Provider momentarily unavailable: the group waits, the player retries -->
            <template v-if="group.odds.some(o => o.bb_unavailable)">
              <div class="bb-redundant-block">
                <span class="bb-redundant-unavailable">{{ $t('errors.betBuilderUnavailable') }}</span>
                <span class="bb-redundant-hint">
                  <v-icon x-small color="warning" class="mr-1">mdi-timer-sand</v-icon>
                  {{ $t('errors.betBuilderUnavailableReason') }}
                </span>
                <button type="button" class="bb-retry-btn" @click.stop="retryBetBuilder(group)">{{ $t('bb_retry') }}</button>
              </div>
            </template>
            <template v-else-if="group.odds.some(o => o.bb_redundant)">
              <div class="bb-redundant-block">
                <span class="bb-redundant-unavailable">{{ $t('bb_odds_unavailable') }}</span>
                <span class="bb-redundant-hint">
                  <v-icon x-small color="warning" class="mr-1">mdi-alert</v-icon>
                  {{ $t('bb_remove_redundant') }}
                </span>
              </div>
            </template>
            <div v-else class="bet-odds odd-badge equal">
              {{ getBetBuilderOdd(group) | odds_type_filter }}
            </div>
          </div>
        </div>

        <!-- Righe normali (non BetBuilder) -->
        <template v-else>
          <div
              v-for="(odd, idx) in group.odds"
              :key="odd.unique_id"
              class="bet-row"
              :class="{
                'culprit-min': isCulprit(odd) === 'min',
                'culprit-max': isCulprit(odd) === 'max',
                'is-unavailable': odd.to_remove,
                'is-suspended': odd.suspended && !odd.to_remove
              }"
          >
            <!-- Overlay per quote non disponibili -->
            <div class="unavailable-overlay" v-if="odd.to_remove"></div>

            <!-- Badge "sospesa": stato transitorio, la quota può tornare giocabile -->
            <div class="suspended-badge" v-if="odd.suspended && !odd.to_remove">
              <span class="suspended-badge-dot"></span>
              <span>{{ $t('errors.oddSuspended') }}</span>
            </div>

            <!-- Divider tra le quote dello stesso evento -->
            <div v-if="idx > 0" class="odd-divider"></div>

            <div class="bet-row-inner">

              <div class="bet-line bet-line--sub">
                <span class="bet-market">
                  {{ odd.market_alias || odd.market_name }}
                  <span class="line-val" v-if="getOddLine(odd)">{{ getOddLine(odd) }}</span>
<!--                  <span class="baseline-val" v-if="odd.baseLine != null && odd.baseLine !== ''">{{ odd.baseLine }}</span>-->
                  <div v-if="odd.earlyPayout && odd.earlyPayoutMargin" class="match-badges">
                    <FeatureBadge badge-key="earlyPayout" />
                  </div>
                </span>
              </div>

              <!-- riga 1: selection-text ↔ odd value (stesso font-size) -->
              <div class="bet-line">
                <span class="selection-text" :title="odd.event_description">
                  {{ getOutcomeName(odd.competitors, odd, odd.unique_id) }}
                  <span class="specifier-val" v-if="hasSpecifier(odd)">
                    {{ formatSpecifier(odd.specifiers) }}
                  </span>
                </span>
                <div class="bet-odds" :class="['odd-badge', (odd.type_change != undefined) ? odd.type_change : 'equal']">
                  <!-- Pre-boost strikethrough: only when the outcome was
                       actually boosted server-side. Tight inline layout
                       so it fits the narrow sidebar; the widget uses the
                       big framed variant, the cart uses this compact one
                       to keep the line height at 14px. -->
                  <template v-if="odd.boostApplied === true && odd.oddsPreBoost">
                    <span class="odd-was">
                      {{ Number(odd.oddsPreBoost) | odds_type_filter }}
                    </span>
                    <v-icon x-small class="odd-boost-arrow">mdi-arrow-right-thin</v-icon>
                  </template>
                  <span :class="{ 'odd-boosted-value': odd.boostApplied === true && odd.oddsPreBoost }">
                    {{ odd.outcome_odd_value | odds_type_filter }}
                  </span>
                  <OddBoostBadge :odd="odd" size="sm"/>
                </div>
                <div v-if="group.odds.length > 1" class="bet-remove sheet-remove" :aria-label="$t('remove')" @click.stop="removeOdd(odd.unique_id)">
                  <v-icon small class="btn-remove">mdi-close</v-icon>
                </div>
              </div>
            </div>

            <!-- FOOTER: quote cambiate / sistema / rimossa -->
            <div v-if="odd.changed || odd.to_remove || getBet.type === 'system'" class="bet-row-footer">

              <!-- CHANGED_ODD_VALUE: mostra old→new con bottoni Rimuovi / Accetta -->
              <div v-if="odd.changed && odd.type_change === 'changed_odd_value'" key="changed-val" class="changed-odd-value-block">
                <div class="changed-odd-info">
                  <v-icon x-small color="warning" class="mr-1">mdi-swap-vertical</v-icon>
                  <span class="old-odd-val">{{ parseFloat(odd.oldOdd).toFixed(2).replace('.', ',') }}</span>
                  <v-icon x-small class="mx-1 arrow-icon">mdi-arrow-right</v-icon>
                  <span class="new-odd-val">{{ parseFloat(odd.newOdd).toFixed(2).replace('.', ',') }}</span>
                </div>
                <div class="changed-odd-actions">
                  <button class="btn-odd-action btn-odd-remove" @click.stop="removeOdd(odd.unique_id)">
                    {{ $t('remove') || 'Rimuovi' }}
                  </button>
                  <button class="btn-odd-action btn-odd-accept" @click.stop="acceptOddVariation(odd.unique_id)">
                    {{ $t('acceptOddChange') || 'Accetta' }}
                  </button>
                </div>
              </div>

              <!-- Altri tipi di variazione: semplice pulsante accetta -->
              <div v-else-if="odd.changed" key="changed-alert" class="update-alert" @click.stop="acceptOddVariation(odd.unique_id)">
                <v-icon x-small color="success" class="mr-1">mdi-cached</v-icon>
                <span>{{ $t('quotaVariata') || 'Accetta Cambio' }}</span>
              </div>

              <div v-if="getBet.type === 'system'" class="system-controls">
                <button
                    class="base-selector"
                    :class="{ 'active': checkFixedBase(odd.unique_id) }"
                    @click="addFixedBase(odd.unique_id)"
                >
                  F
                </button>
              </div>
              <div class="error-text" v-if="odd.to_remove">
                {{ $t('oddNotAvailable') }}
              </div>
            </div>
          </div>
        </template>
      </div>


      <div
          class="bonus-card"
          :class="{ 'bonus-card--action': cartSheet }"
          v-if="getSettings.sm_bonus && nextBonus && !getSelectedFreeBet && (getBet.type === 'multiple' || getBet.type === 'single')"
          @click="cartSheet && $root.$emit('close_sidebar_right', true)"
      >
        <div class="bonus-left">
          <div class="bonus-icon">
            <v-icon size="20">mdi-gift-outline</v-icon>
          </div>
          <div class="bonus-title" v-html="$t('nextBonusHint', { n: nextBonus.eventsNeeded, pct: nextBonus.percentage, minOdd: nextBonus.minOddValue })"/>

        </div>
        <v-icon v-if="cartSheet" class="bonus-chevron">mdi-chevron-right</v-icon>

      </div>

    </div>
  </section>

</template>

<script>
  import { mapActions, mapGetters } from 'vuex';
  import {isMobileOnly} from "mobile-device-detect";
  import { MARKET_NOT_NAME_IN_OUTCOME } from '../../constants/marketConstants';
  import OddBoostBadge from '@/components/generic/OddBoostBadge.vue';

  import FeatureBadge from '@/components/badges/FeatureBadge.vue';
  import BetBuilderBadge from '@/components/badges/BetBuilderBadge.vue';
  export default {
    name: 'BetOddsSummary',
    inject: { cartSheet: { default: false } },
    components: {OddBoostBadge, FeatureBadge, BetBuilderBadge},
    data() {
      return {
        sportPath: process.env.VUE_APP_URL_CLOUDFRONT + '/svg/sports/icons-color/',
        onForceUpdateHandler: null // Aggiunto per salvare la reference
      };
    },
    mounted() {
      this.$store.commit('cart/SET_SYSTEM_FIXED_BASES', []);
      this.onForceUpdateHandler = () => {
        this.$forceUpdate();
      };
      this.$root.$on('forceUpdateOddsSummary', this.onForceUpdateHandler);
    },
    beforeDestroy() {
      // FONDAMENTALE: rimuovi il listener quando il componente muore!
      if (this.onForceUpdateHandler) {
        this.$root.$off('forceUpdateOddsSummary', this.onForceUpdateHandler);
      }
    },
    computed: {
      ...mapGetters({
        'getBet': 'cart/getBet',
        'getUserType': 'user/getType',
        'getCashier': 'user/getCashier',
        'getSettings': 'cart/getSettings',
        getBonusRules: "cart/getBonusRules",
        // Con una freebet il bonus multipla non si applica: niente suggerimento "aggiungi eventi".
        getSelectedFreeBet: "cart/getSelectedFreeBet",
        getCompetitorsInOutcome: 'settings/getCompetitorsInOutcome',
      }),
      cartErrors() {
        return this.getBet.errors || [];
      },
      groupedOdds() {
        const map = new Map();
        for (const odd of this.getBet.odds) {
          const key = odd.bet_builder_id || odd.event_source || odd.unique_id;
          if (!map.has(key)) {
            map.set(key, {
              event_source: key,
              event_description: odd.event_description,
              is_bet_builder: !!odd.bet_builder_id,
              bet_builder_id: odd.bet_builder_id || null,
              odds: [],
            });
          }
          map.get(key).odds.push(odd);
        }
        return Array.from(map.values());
      },
      nextBonus() {
        if (!this.getBonusRules || !this.getBonusRules.length) return null;
        const betContext = this.getBet.context;
        if (!betContext) return null;
        // Mirror cartUtilities.getBonus: prematch=0, live/mixed=1
        const ruleContext = betContext === 'prematch' ? 0 : 1;
        const currentMult = +this.getBet._multiple_detail.multiplicity || 0;
        const nextRule = this.getBonusRules
            .filter(r => +r.multiplicity > currentMult && +r.percentage > 0 && +r.context === ruleContext)
            .sort((a, b) => +a.multiplicity - +b.multiplicity)[0];
        if (!nextRule) return null;
        return {
          eventsNeeded: +nextRule.multiplicity - currentMult,
          percentage: +nextRule.percentage,
          minOddValue: +nextRule.minOddValue || null,
        };
      },
    },
    methods: {
      ...mapActions({
        'removeOddFromCart': 'cart/removeOddFromCart',
        'refreshCart': 'cart/refreshCart',
        'checkFixedBaseSystem': 'cart/checkFixedBaseSystem',
        'acceptOdd': 'cart/acceptOdd',
        fetchMixedFeaturedEventsV3: 'featuredEvents/fetchMixedFeaturedEventsV3',
        fetchEventDetailV3: 'detailPrematch/fetchEventDetailV3',
        fetchEventLiveDetailV5: 'detailLive/fetchEventLiveDetailV5',
        setLoadingPreview: 'odds/setLoadingPreview',
        setViewMode: 'ui/setViewMode',
      }),
      async goToDetail(event) {
        if (window.innerWidth < 1200 || window.forceMobileView === 'true') {
          window.scrollTo({top: 0, behavior: 'smooth'});
        }


        const evtId = event.id || event.eventId || event.event_source;

        const homeTeam = event.home || (event.competitors && event.competitors[0] ? event.competitors[0].name : '');
        const awayTeam = event.away || (event.competitors && event.competitors[1] ? event.competitors[1].name : '');
        const homeId = event.home_id || (event.competitors && event.competitors[0] ? event.competitors[0].id : undefined);
        const awayId = event.away_id || (event.competitors && event.competitors[1] ? event.competitors[1].id : undefined);

        const tournamentName = event.tournament.name
        const tournamentId = event.tournament.id
        const sportId = event.sport.id
        const categoryId = event.category.id

        if (event.type === 'live') {
          this.setLoadingPreview({status: true});
          if (isMobileOnly) {
            this.$root.$emit('close_sidebar_right', true);
          }
          await this.fetchEventLiveDetailV5({
            source: evtId,
            group: 0,
            sport: sportId,
            tournament: tournamentId,
            status: event.status,
            sportName: event.sportName,
            categoryName: event.categoryName,
            tournamentName: tournamentName,
            competitorHome: homeTeam,
            competitorAway: awayTeam,
            competitorHomeId: homeId,
            competitorAwayId: awayId,

          });
          this.setViewMode({mode: 'detail'});
          this.setLoadingPreview({status: false});



        } else {
          // Prematch event → existing flow
          this.setLoadingPreview({status: true});
          if (isMobileOnly) {
            this.$root.$emit('close_sidebar_right', true);
          }

          const scheduled = event.scheduled || event.scheduledDate;
          await this.fetchEventDetailV3({
            source: evtId,
            group: null,
            sport: sportId,
            category: categoryId,
            tournament: tournamentId,
            startEventTime: scheduled,
            tournamentName: tournamentName,
            competitorHome: homeTeam,
            competitorAway: awayTeam,
            competitorHomeId: homeId,
            competitorAwayId: awayId,

          });
          this.setLoadingPreview({status: false});
        }
      },
      positionTooltip(rect) {
        this.$nextTick(() => {
          const tooltipEl = this.$el.querySelector('.custom-tooltip');
          if (!tooltipEl) return;

          const tipW = tooltipEl.offsetWidth;
          const tipH = tooltipEl.offsetHeight;
          const vw = window.innerWidth;
          const gap = 8;

          // Verticale: sopra se c'è spazio, altrimenti sotto
          let top, transformY;
          if (rect.top - tipH - gap >= 0) {
            top = rect.top - gap;
            transformY = '-100%';
            this.tooltipPlacement = 'above';
          } else {
            top = rect.bottom + gap;
            transformY = '0%';
            this.tooltipPlacement = 'below';
          }

          // Orizzontale: centrato sull'elemento, clampato ai bordi
          const rawLeft = rect.left + rect.width / 2;
          let left, transformX;
          if (rawLeft - tipW / 2 < gap) {
            left = gap;
            transformX = '0%';
          } else if (rawLeft + tipW / 2 > vw - gap) {
            left = vw - gap - tipW;
            transformX = '0%';
          } else {
            left = rawLeft;
            transformX = '-50%';
          }

          this.tooltipStyle = {
            top: top + 'px',
            left: left + 'px',
            transform: `translateX(${transformX}) translateY(${transformY})`,
            opacity: '1',
          };
        });
      },
      showTooltip(nativeEvent, eventId, content) {
        const rect = nativeEvent.currentTarget.getBoundingClientRect();
        this.tooltipContent = content;
        this.tooltipStyle = { top: '-9999px', left: '-9999px' };
        this.activeTooltipId = eventId;
        this.positionTooltip(rect);
      },
      hideTooltip() {
        this.activeTooltipId = null;
        this.tooltipStyle = { ...this.tooltipStyle, opacity: '0' };
      },
      toggleMobileTooltip(nativeEvent, eventId, content) {
        if (this.activeMobileTooltipId === eventId) {
          this.activeMobileTooltipId = null;
          this.tooltipStyle = { ...this.tooltipStyle, opacity: '0' };
        } else {
          const rect = nativeEvent.currentTarget.getBoundingClientRect();
          this.tooltipContent = content;
          this.tooltipStyle = { top: '-9999px', left: '-9999px' };
          this.activeMobileTooltipId = eventId;
          this.positionTooltip(rect);
        }
      },
      getOutcomeName(c, o, unique_id) {
        if (MARKET_NOT_NAME_IN_OUTCOME.includes(unique_id)) {
          return o.outcome_alias || o.outcome_name;
        }
        const outcome = o.outcome_name?.toLowerCase();
        if (this.getCompetitorsInOutcome) {
          if (outcome === 'local') return c?.[0]?.name || o.outcome_alias || o.outcome_name;
          if (outcome === 'visitante') return c?.[1]?.name || o.outcome_alias || o.outcome_name;
          if ((o.outcome_name === "1" || o.outcome_name === "2") && o.id_market != "128") {
            for (let i = 0; i < c.length; i++) {
              if (c[i].position === o.outcome_name) return c[i].name;
            }
          }
        }
        return o.outcome_alias || o.outcome_name;
      },

      stripScoreState(str) {
        if (!str) return str;
        return String(str).replace(/\s*\(\d+-\d+\)\s*$/, '').trim();
      },

      // todo hey fix me with this if dirty fix !!!
      getOddLine(odd) {
        if (    odd.id_market === '13' || odd.id_market === 13
                || odd.id_market === "61" || odd.id_market === 61
                || odd.id_market === "935" || odd.id_market === 935
                || odd.id_market === "1694" || odd.id_market === 1694
                || odd.id_market === "1693" || odd.id_market === 1693
                || odd.id_market === "1692" || odd.id_market === 1692
                || odd.id_market === "1691" || odd.id_market === 1691
                || odd.id_market === "433" || odd.id_market === 433
                || odd.id_market === "386" || odd.id_market === 386
        ) {
          return (odd.baseLine != null && odd.baseLine !== '') ? this.stripScoreState(odd.baseLine) : null;
        }
        if (odd.line && odd.line !== '') return this.stripScoreState(odd.line);
        if (odd.baseLine && odd.baseLine !== '') return this.stripScoreState(odd.baseLine);
        if (odd.specifiers && odd.specifiers !== '') {
          const part = odd.specifiers.split(' ')[0];
          return isNaN(part.charAt(0)) && part.charAt(0) !== '-' && part.charAt(0) !== '+' ? null : part;
        }
        return null;
      },

      hasSpecifier(odd) {
        return odd.specifiers && odd.specifiers.includes('(');
      },

      formatSpecifier(val) {
        const match = val.match(/\(([^)]+)\)/);
        return match ? match[0] : '';
      },

      isCulprit(odd) {
        if (!this.cartErrors.length) return false;
        const minError = this.cartErrors.find(e => e.type === 'MULTIPLICITY_TOO_LOW');
        const maxError = this.cartErrors.find(e => e.type === 'MULTIPLICITY_TOO_HIGH');
        if (minError && (+odd.multiplicity_min || 1) === minError.globalMin) return 'min';
        const oddMax = (+odd.multiplicity_max > 0) ? +odd.multiplicity_max : Infinity;
        if (maxError && oddMax === maxError.globalMax) return 'max';
        return false;
      },

      async acceptOddVariation(outcomeUniqueId) {
        await this.acceptOdd({'unique_id': outcomeUniqueId});
        await this.refreshCart();
        this.forceUpdateOddsSummary();
        this.$root.$emit('forceUpdateSystem');
        this.$root.$emit('forceUpdateSingleMultiple');
        this.$root.$emit('forceUpdateIntegral');
      },

      // "Already covered by Newcastle Jets FC (1X2): remove this selection"; the generic
      // text when the covering selection is not in the group.
      redundantLegHint(group, odd) {
        const by = group.odds.find(o => String(o.unique_id) === String(odd.bb_implied_by));
        if (!by) return this.$t('bb_remove_redundant');
        const selection = `${this.getOutcomeName(by.competitors, by, by.unique_id)} (${by.market_alias || by.market_name})`;
        return this.$t('bb_redundant_leg', {selection});
      },
      retryBetBuilder(group) {
        this.$store.dispatch('cart/retryBetBuilderGroup', { eventSource: group.odds[0].event_source });
      },
      async removeOdd(outcomeUniqueId) {
        await this.removeOddFromCart({'unique_id': outcomeUniqueId});
        await this.checkFixedBaseSystem({'unique_id': outcomeUniqueId});
        this.$root.$emit('forceUpdateSystem');
        this.$root.$emit('forceUpdateSingleMultiple');
        this.$root.$emit('forceUpdateIntegral');
      },

      async removeGroup(group) {
        for (const odd of group.odds) {
          await this.removeOddFromCart({ unique_id: odd.unique_id, skipBetBuilderRecalc: group.is_bet_builder });
        }
        await this.checkFixedBaseSystem({ unique_id: group.odds[0].unique_id });
        this.$root.$emit('forceUpdateSystem');
        this.$root.$emit('forceUpdateSingleMultiple');
        this.$root.$emit('forceUpdateIntegral');
      },

      getBetBuilderOdd(group) {
        const main = group.odds.find(o => parseFloat(o.outcome_odd_value) !== 1);
        return main ? main.outcome_odd_value : (group.odds[0] ? group.odds[0].outcome_odd_value : 0);
      },

      async addFixedBase(outcomeUniqueId) {
        await this.checkFixedBaseSystem({'unique_id': outcomeUniqueId});
        this.$root.$emit('forceUpdateSystem');
      },

      checkFixedBase(outcomeUniqueId) {
        return this.getBet._system_detail.fixed_bases.includes(outcomeUniqueId);
      },

      eventTeams(odd) {
        const c = odd.competitors;
        if (Array.isArray(c) && c.length === 2 && c[0] && c[1]) return [c[0].name, c[1].name];
        return [];
      },
      formatEventTime(ms) {
        const date = new Date(Number(ms));
        if (isNaN(date.getTime())) return '';
        const locale = this.$i18n && this.$i18n.locale;
        const time = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
        const today = new Date();
        if (date.toDateString() === today.toDateString()) return time;
        return date.toLocaleDateString(locale, { day: 'numeric', month: 'short' }) + ' · ' + time;
      },
            forceUpdateOddsSummary() {
        this.$forceUpdate();
      },

      getTime(dateTimeMillis) {
        const date = new Date(dateTimeMillis);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) + ' - ' + date.toLocaleDateString();
      }
    }
  };
</script>

<style scoped>
.bet-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0;
}

/* Card singola o raggruppata */
.bet-card {
  background: var(--bg-card);
  border-radius: 6px;
  overflow: hidden;
  transition: background 0.2s;
  border: 1px solid hsla(0, 0%, 100%, .04);
  cursor: pointer;
}

.bet-card--multi {
}

.bet-card:hover .event-group-name {
  color: var(--color-primary-light);
}

/* Card multi-quota: bordo sinistro accent per distinguerla visivamente */


/* Header sempre presente */
.event-group-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px 5px 12px;
}

.event-group-name {
  flex: 1;
  font-size: var(--font-size-2xl);
  font-weight: 700;
  color: var(--text-platform);
}

.header-remove {
  margin-left: auto;
  padding-top: 0;
}

/* Ogni riga quota */
.bet-row {
  position: relative;
}

/* Divisore sottile tra quote dello stesso evento */
.odd-divider {
  height: 1px;
  background: var(--border-color, rgba(255,255,255,0.06));
  margin: 0 12px;
}


.bet-row-footer {
  padding: 0 12px 8px 12px;
}

.bet-remove {
  width: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0.7;
  padding-top: 2px;
}

.bet-remove:hover {
  opacity: 1;
}

/* riga orizzontale: sinistra flex:1, destra allineato */
.bet-row-inner {
  display: flex;
  flex-direction: column;
  padding: 8px 12px;
  gap: 3px;
}

.bet-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.bet-line--sub {
  align-items: center;
}

/* riga 1 sinistra: selection-text */
.selection-text {
  font-size: var(--font-size-4xl);
  font-weight: 600;
  color: var(--text-platform);
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.specifier-val {
  font-size: var(--font-size-xl);
  font-weight: 400;
  color: var(--text-op-50-platform);
  margin-left: 4px;
}

/* riga 1 destra: odd value — stesso font-size di selection-text */
.bet-odds {
  font-size: var(--font-size-4xl);
  font-weight: 700;
  color: var(--color-accent);
  white-space: nowrap;
  flex-shrink: 0;
  display: inline-flex;
  align-items: baseline;
  gap: 3px;
}

/* Boost mini-compare in the cart row — same intent as the widget's
   `.price-compare` (was → now) but sized down to fit the narrow
   sidebar without pushing the row height. Struck-through pre-boost
   value, red diagonal stroke matches the visual on the widget so the
   two surfaces feel like one product. */
.odd-was {
  font-size: var(--font-size-md);
  font-weight: 600;
  color: var(--text-op-50-platform);
  text-decoration: line-through;
  text-decoration-color: rgba(239, 68, 68, 0.85);
  text-decoration-thickness: 1.5px;
  opacity: 0.85;
}
.odd-boost-arrow {
  transform: translateY(1px);
  color: var(--color-accent) !important;
  font-size: var(--font-size-2xl) !important;
}
/* When the current price is a boosted one, tint it green to match the
   widget's boost-callout — no color change on non-boosted rows so the
   normal cart look is untouched. */
.odd-boosted-value {
  color: var(--color-accent);
  text-shadow: 0 0 6px rgba(6, 214, 160, 0.25);
}

/* riga 2 sinistra: market */
.bet-market {
  font-size: var(--font-size-xl);
  color: var(--text-op-50-platform);
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.live-badge {
  padding: 2px 6px!important;
}

.baseline-val {
  font-size: var(--font-size-md);
  font-weight: 600;
  color: var(--color-primary-light);
  margin-left: 2px;
}

/* Errori molteplicità */
.culprit-min { border-left-color: var(--warning); }
.culprit-max { border-left-color: var(--danger); }
.is-unavailable { opacity: 0.6; }

/* ── ODD_SUSPENDED: stato transitorio, riga giocabile ma con avviso ── */
.is-suspended {
  border-left-color: var(--warning, #f59e0b) !important;
}

.suspended-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  margin: 4px 0 0 4px;
  padding: 2px 8px;
  border-radius: 10px;
  background: rgba(245, 158, 11, 0.12);
  border: 1px solid rgba(245, 158, 11, 0.35);
  color: var(--warning, #f59e0b);
  font-size: var(--font-size-md);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.suspended-badge-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--warning, #f59e0b);
  animation: bet-row-suspended-pulse 1.6s ease-in-out infinite;
}

@keyframes bet-row-suspended-pulse {
  0%, 100% { opacity: 0.4; transform: scale(0.85); }
  50%      { opacity: 1;   transform: scale(1); }
}

.btn-remove {
  color: var(--text-platform) !important;
  cursor: pointer;
  transition: color 0.2s;
}
.btn-remove:hover {
  color: var(--danger) !important;
}

.odd-badge.up   { color: var(--success); }
.odd-badge.down { color: var(--danger); }

.base-selector {
  background: transparent;
  border: 1.5px solid var(--color-primary);
  color: var(--color-primary);
  font-size: var(--font-size-md);
  width: 18px;
  height: 24px;
  padding: 0;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 800;
  transition: all 0.18s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.base-selector.active {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: #fff;
}

.update-alert {
  font-size: var(--font-size-xl);
  color: var(--success);
  cursor: pointer;
  display: flex;
  align-items: center;
  font-weight: 600;
}

/* ── CHANGED_ODD_VALUE: banner con vecchio→nuovo valore e due azioni ── */
.changed-odd-value-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 4px 0 2px;
}

.changed-odd-info {
  display: flex;
  align-items: center;
  font-size: var(--font-size-xl);
  font-weight: 600;
  gap: 2px;
}

.old-odd-val {
  color: var(--text-op-50-platform);
  text-decoration: line-through;
}

.arrow-icon {
  color: var(--text-op-50-platform) !important;
}

.new-odd-val {
  color: var(--warning, #f59e0b);
  font-weight: 700;
}

.changed-odd-actions {
  display: flex;
  gap: 6px;
}

.btn-odd-action {
  flex: 1;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: var(--font-size-md);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s, transform 0.1s;
}

.btn-odd-action:hover {
  opacity: 0.85;
  transform: translateY(-1px);
}

.btn-odd-remove {
  background: rgba(239, 68, 68, 0.15);
  color: var(--danger, #ef4444);
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.btn-odd-accept {
  background: rgba(34, 197, 94, 0.15);
  color: var(--success, #22c55e);
  border: 1px solid rgba(34, 197, 94, 0.3);
}

.error-text {
  color: var(--danger);
  font-size: var(--font-size-xl);
  font-weight: bold;
}

.unavailable-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: var(--bg-overlay);
  border-radius: 8px;
  pointer-events: none;
}

.sport-icon { height: 12px; width: 12px; }

/* ── BetBuilder card ─────────────────────────────────────── */
.bet-card--bb {
}

.bb-badge-cart {
  flex-shrink: 0;
  padding: 2px 4px;
  border-radius: 4px;
  font-size: var(--font-size-md);
  font-weight: 800;
  line-height: 13px;
  color: var(--bg-body);
  background: var(--color-primary-light);
}

/* Timeline verticale nel carrello */
.bb-timeline-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 8px 12px 10px 24px;
  position: relative;
}



/* Sostituisci il :not(:last-child) di prima con questo */
.bb-timeline-row:not(.bb-timeline-row-last)::before {
  content: '';
  position: absolute;
  left: -9px;
  top: 4px;
  bottom: -10px;
  width: 1px;
  background: var(--color-primary-light, #21a8f7);
  opacity: 0.4;
  z-index: 0;
}

.bb-timeline-dot {
  position: absolute;
  left: -12px;
  top: 4px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-primary-light, #21a8f7);
  box-shadow: 0 0 4px rgba(33, 168, 247, 0.6);
  z-index: 1; /* <-- Aggiungi questo */
}

.bb-timeline-row {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.bb-timeline-dot {
  position: absolute;
  left: -12px;
  top: 4px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-primary-light, #21a8f7);
  box-shadow: 0 0 4px rgba(33, 168, 247, 0.6);
}

.bb-timeline-content {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.bb-outcome-name {
  font-size: var(--font-size-2xl);
  font-weight: 600;
  color: var(--text-platform);
}

.bb-market-name {
  font-size: var(--font-size-md);
  color: var(--text-op-50-platform);
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 4px;
}

.bb-line-val {
  color: var(--text-platform);
  font-weight: 700;
}

.bb-combo-odd-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 4px;
  padding-top: 8px;
  border-top: 1px solid rgba(255,255,255,0.07);
}

.bb-combo-label {
  font-size: var(--font-size-xl);
  font-weight: 600;
  text-transform: uppercase;
  color: var(--text-op-50-platform);
  letter-spacing: 0.04em;
}

.bb-combo-odd-row .bet-odds{
  color: var(--color-primary-light);
}

.bb-redundant-block {
  display: flex;
  flex-direction: column;
  gap: 3px;
  width: 100%;
}

.bb-redundant-unavailable {
  font-size: var(--font-size-2xl);
  font-weight: 700;
  color: var(--text-op-50-platform);
}

.bb-timeline-row--redundant {
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.bb-timeline-row--redundant .bb-outcome-name {
  color: var(--v-warning-base, #fb8c00);
  text-decoration: line-through;
}

.bb-redundant-leg-hint {
  display: flex;
  align-items: center;
  font-size: var(--font-size-md);
  font-weight: 500;
  color: var(--v-warning-base, #fb8c00);
}

.bb-redundant-leg-remove {
  flex-shrink: 0;
  cursor: pointer;
}

.bb-retry-btn {
  align-self: flex-start;
  margin-top: 4px;
  padding: 2px 10px;
  font-size: var(--font-size-md);
  font-weight: 600;
  color: var(--color-primary-light, #21a8f7);
  background: transparent;
  border: 1px solid currentColor;
  border-radius: 4px;
  cursor: pointer;
}

.bb-redundant-hint {
  display: flex;
  align-items: center;
  font-size: var(--font-size-xl);
  font-weight: 500;
  color: var(--v-warning-base, #fb8c00);
  gap: 2px;
}

.bonus-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 6px 12px 12px;
  border-radius: 6px;
  border: 1px dashed var(--hsla-1);
  transition: all 0.2s ease;
}

/* hover (solo desktop) */


.bonus-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.bonus-icon {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius:100px;
  background: var(--color-primary-op);
  color: var(--color-primary-light);

}

.bonus-icon .v-icon{
  color: var(--color-primary-light)!important;
}

.bonus-text {
  display: flex;
  flex-direction: column;
}

.bonus-title {
  font-size: var(--font-size-2xl);
  color: var(--text-op-50-platform);
}

.bonus-title b{
  color: var(--color-primary-light);
}

.bonus-highlight {
  font-size: var(--font-size-4xl);
  font-weight: 600;
  color: #00e6a8; /* verde principale */
}

.bonus-arrow {
  font-size: var(--font-size-7xl);
  color: rgba(255, 255, 255, 0.4);
  transition: transform 0.2s ease;
}

.bonus-card:hover .bonus-arrow {
  transform: translateX(4px);
  color: #00e6a8;
}

/* ── Card evento (mobile e desktop) ──────────────────────── */
.bet-list--cards {
  gap: 12px;
  padding: 14px 0 12px;
}

.bet-list--cards .bet-card {
  border-radius: 12px;
  border-color: var(--border-color, rgba(255,255,255,0.06));
}

.bet-list--cards .event-group-header {
  gap: 10px;
  padding: 14px 14px 4px 16px;
}

.bet-list--cards .sport-icon {
  width: 18px;
  height: 18px;
}

.bet-list--cards .event-group-name {
  font-size: var(--font-size-2xl);
  line-height: 1.3;
}

.bet-list--cards .bet-remove {
  width: 28px;
  height: 28px;
  margin-right: -6px;
  padding: 0;
  border-radius: 50%;
}

.bet-list--cards .bet-remove:active {
  background: rgba(255,255,255,0.08);
}

.bet-list--cards .bet-row-inner {
  padding: 8px 16px 14px;
  gap: 8px;
}

.bet-list--cards .bet-market {
  font-size: var(--font-size-2xl);
  gap: 8px;
}

.bet-list--cards .live-badge {
  padding: 3px 8px !important;
  border-radius: 4px;
}

.bet-list--cards .selection-text {
  font-size: var(--font-size-4xl);
  font-weight: 700;
}

.bet-list--cards .bet-odds {
  font-size: var(--font-size-4xl);
}

.bet-list--cards .bet-row-footer {
  padding: 0 16px 14px;
}

.bet-list--cards .btn-odd-action {
  padding: 8px;
  border-radius: 8px;
}

.bet-list--cards .bonus-card {
  padding: 14px 12px 14px 14px;
  border-radius: 12px;
  border-color: var(--color-primary-light);
  border-color: color-mix(in srgb, var(--color-primary-light) 45%, transparent);
}

.bonus-card--action {
  cursor: pointer;
}

.bonus-card--action:active {
  transform: scale(0.99);
}

.bet-list--cards .bonus-icon {
  width: 36px;
  height: 34px;
  flex-shrink: 0;
}

.bet-list--cards .bonus-title {
  font-size: var(--font-size-2xl);
  line-height: 1.4;
}

.bet-list--cards .bonus-title >>> b {
  font-size: var(--font-size-2xl);
}

.bonus-chevron {
  flex-shrink: 0;
  color: var(--text-platform) !important;
}

/* ── Card "giocata in evidenza" ──────────────────────────── */
/* Esito + quota in alto, mercato sotto, partita come contesto in fondo */
.bet-list--cards .bet-row-inner {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  grid-template-areas:
    "pick odd remove"
    "market odd remove";
  align-items: center;
  column-gap: 10px;
  row-gap: 3px;
  padding: 12px 10px 14px 16px;
}

.bet-list--cards .bet-line {
  display: contents;
}

/* Una sola giocata: la X è in alto a destra, niente colonna "remove" */
.bet-list--cards .bet-card:not(.bet-card--multi) .bet-row-inner {
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas:
    "pick odd"
    "market odd";
  padding-right: 16px;
}

.sheet-match__remove {
  align-self: flex-start;
  margin: -4px -6px 0 0 !important;
}

.bet-list--cards .selection-text {
  grid-area: pick;
  font-size: var(--font-size-5xl);
  font-weight: 700;
  line-height: 1.3;
}

.bet-list--cards .bet-market {
  grid-area: market;
  font-size: var(--font-size-2xl);
  font-weight: 500;
  text-transform: none;
}

.bet-list--cards .bet-line .bet-odds {
  grid-area: odd;
  min-width: 60px;
  justify-content: center;
  padding: 9px 12px;
  border-radius: 10px;
  background: var(--sidebar);
  border: 1px solid var(--border-color, rgba(255,255,255,0.08));
  font-size: var(--font-size-5xl);
  font-weight: 800;
}

.bet-list--cards .sheet-remove {
  grid-area: remove;
  width: 28px;
  height: 28px;
  margin: 0;
  padding: 0;
  opacity: 0.6;
}

.bet-list--cards .odd-divider {
  margin: 0 16px;
}

.sheet-match {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 14px 12px 16px;
}

/* Linea che parte dall'icona dello sport e sfuma verso destra */
.sheet-match::after {
  content: '';
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 0;
  height: 1px;
  background: linear-gradient(90deg, var(--text-op-50-platform) 0%, transparent 85%);
  opacity: 0.5;
  pointer-events: none;
}

.sheet-match__icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  opacity: 0.85;
}

.sheet-match__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sheet-match__teams {
  font-size: var(--font-size-2xl);
  font-weight: 600;
  line-height: 1.35;
  color: var(--text-platform);
  /* Nomi lunghi vanno a capo (max 2 righe) invece di essere troncati */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.sheet-match__vs {
  margin: 0 6px;
  font-weight: 400;
  color: var(--text-op-50-platform);
}

.sheet-match__meta {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-size: var(--font-size-xl);
  color: var(--text-op-50-platform);
  white-space: nowrap;
}

.sheet-match__tournament {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sheet-match__live {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 6px;
  border-radius: 4px;
  background: rgba(239, 68, 68, 0.15);
  color: var(--danger, #ef4444);
  font-size: var(--font-size-md);
  font-weight: 800;
  text-transform: uppercase;
}

.sheet-match__live-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  animation: bet-row-suspended-pulse 1.4s ease-in-out infinite;
}
</style>
